import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FilterState } from '../../FilterDrawer/types';
import type { FilterModel } from '../../ColumnFilters/types';
import { STATUS_FILTER_LABELS } from '../../FilterDrawer/constants';
import {
  exportJobsList,
  fetchJobScheduleByHour,
  fetchJobStatusByType,
  fetchFilterDetails,
  fetchJobsList,
  fetchScheduleKpi,
  fetchSummary,
  fetchExportCount,
  fetchCustomStatusCountKpi,
  getRunDistribution,
  getRunTrend,
  getRunTrendAvg,
} from 'service/service';
import { formatDateRangeLabel } from 'utils/common-util';
import { useDashboardFilterPersistence } from 'hooks/useDashboardFilterPersistence';
import { createNamespacedFilterCache } from '../utils/filterCache';
import {
  transformJobTypeByStatusData,
  transformRunOvertimeTrendData,
  transformRunTrendAvgToCards,
  transformScheduleByHourData,
  CUSTOM_STATUS_TILE_SET,
  CUSTOM_STATUS_OPTIONS,
  normalizeStatus,
  getInitialPipelineFilters as getInitialFilters,
  getFilterValues as getValues,
  normalizeColumnFiltersForRequest,
  buildPageFilters,
  makePipelineDateRange as makeDateRange,
  type TransformedChartTableData,
  type TransformedRunOvertimeData,
  type OvertimeCard,
} from '../utils/pipelineChartUtils';
import type { TransformedRunDistributionData } from '../RunDistribution';

const STORAGE_PREFIX = 'dataAssets';
const DASHBOARD_CONFIG = {
  dashboardId: 'ijm-data-assets',
  dashboardName: 'Data Assets',
  pageName: 'Data Assets Dashboard',
};

const DEFAULT_FILTER_OPTIONS = {
  jobType: [],
  jobStatus: [],
  program: [],
  sector: [],
  domain: [],
  gdo: [],
  program_job_name: [],
  scheduleCategory: [],
  scheduledForNext: [],
  sourceCategory: [],
  sourceType: [],
  sourceSubcategory: [],
  orchestratorScheduler: [],
  appName: [],
  businessUnit: [],
  businessStream: [],
  department: [],
  capability: [],
  layer: [],
  market: [],
  technology: [],
  dataAssetName: [],
  bronzeTableName: [],
  silverTableName: [],
  goldTableName: [],
  sustainOwner: [],
  sustainProdDate: [],
  resourceName: [],
  resourceGroup: [],
  subscriptionName: [],
  scheduleInfo: [],
  sourceTables: [],
  pecNonPec: [],
  mecNonPec: [],
};

const FILTER_FIELDS = [
  'program',
  'sector',
  'domain',
  'market',
  'dataAssetName',
  'jobType',
  'sourceCategory',
  'sourceType',
  'sourceSubcategory',
  'orchestratorScheduler',
  'businessStream',
  'department',
  'capability',
  'layer',
  'technology',
  'bronzeTableName',
  'silverTableName',
  'goldTableName',
  'sustainOwner',
  'sustainProdDate',
  'resourceName',
  'resourceGroup',
  'subscriptionName',
  'scheduleCategory',
] as const;

const toGlobalFilters = (filters: FilterState) => {
  const globalFilters: Record<string, string[]> = {};
  const jobStatuses = getValues(filters.jobStatus);
  if (jobStatuses.length) globalFilters.jobStatus = jobStatuses;
  const filterKeys = [
    'jobType',
    'program',
    'domain',
    'gdo',
    'market',
    'sourceCategory',
    'sourceType',
    'sourceSubcategory',
    'orchestratorScheduler',
    'businessUnit',
    'businessStream',
    'department',
    'capability',
    'layer',
    'technology',
    'dataAssetName',
    'bronzeTableName',
    'silverTableName',
    'goldTableName',
    'scheduleCategory',
  ] as const;

  filterKeys.forEach((key) => {
    const values = getValues(filters[key] as Array<{ value: string }> | undefined);
    if (values.length) globalFilters[key] = values;
  });

  const sectors = getValues(filters.sector);
  if (sectors.length) globalFilters.sector = sectors;
  return globalFilters;
};

const resolveCustomStatusTile = (activeTile: string, columnFilters: any[], drawerJobStatus: string[] = []) => {
  if (!CUSTOM_STATUS_TILE_SET.has(activeTile)) {
    return { isCustomStatusTile: false, jobStatusList: [activeTile], cleanedColumnFilters: columnFilters };
  }

  const executionFilter = columnFilters.find((filter: any) => filter?.id === 'execution_status');
  const statusValues = (
    executionFilter?.selOptions?.length ? executionFilter.selOptions : executionFilter?.values || []
  ).filter(Boolean);
  const hasNonInProgressStatus = statusValues.some((status: any) =>
    !['all', 'inprogress', 'in progress'].includes(String(status).toLowerCase())
  );
  const onlyInProgress = statusValues.length > 0 && statusValues.every((status: any) =>
    ['all', 'inprogress', 'in progress'].includes(String(status).toLowerCase())
  );
  const drawerStatusValues = drawerJobStatus.map(String);
  const hasNonInProgressDrawerStatus = drawerStatusValues.some((status: any) =>
    !['all', 'inprogress', 'in progress'].includes(String(status).toLowerCase())
  );
  const explicitStatuses = hasNonInProgressStatus
    ? statusValues
    : hasNonInProgressDrawerStatus
      ? drawerStatusValues
      : [];
  const cleanedColumnFilters = onlyInProgress || explicitStatuses.length > 0
    ? columnFilters.filter((filter: any) => filter?.id !== 'execution_status')
    : columnFilters;

  return {
    isCustomStatusTile: true,
    jobStatusList: explicitStatuses.length > 0 ? explicitStatuses : ['InProgress'],
    cleanedColumnFilters,
  };
};

export const useDataAssets = () => {
  const filterCache = useMemo(() => createNamespacedFilterCache(STORAGE_PREFIX), []);
  const savedColumnFilters = filterCache.getColumnFilterState<any>();
  const [activeTile, setActiveTile] = useState('TotalDataAssetRuns');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortingInfo, setSortingInfoState] = useState(() =>
    savedColumnFilters?.sortModel?.[0]
      ? {
          sortBy: savedColumnFilters.sortModel[0].columnKey,
          sortDir: savedColumnFilters.sortModel[0].direction,
        }
      : { sortBy: '', sortDir: '' },
  );
  const [columnFilters, setColumnFilters] = useState<any[]>(
    () => savedColumnFilters?.payload || [],
  );
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(() => {
    const savedFilters = filterCache.getAppliedFilters();
    if (savedFilters) {
      const hasSelections = Object.values(savedFilters).some(
        (value) => Array.isArray(value) && value.length > 0,
      );
      if (hasSelections) return savedFilters;
    }
    return getInitialFilters();
  });
  const [filterOptions, setFilterOptions] = useState<Record<string, any[]>>(DEFAULT_FILTER_OPTIONS);
  const [loadingFilterFields, setLoadingFilterFields] = useState<Record<string, boolean>>({});
  const [summary, setSummary] = useState<any>(null);
  const [scheduledSummary, setScheduledSummary] = useState<any>(null);
  const [customStatusSummary, setCustomStatusSummary] = useState<any>(null);
  const [isCustomStatusKpiLoading, setIsCustomStatusKpiLoading] = useState(true);
  const [activeBottomTab, setActiveBottomTab] = useState<number | null>(null);
  const [runDistribution, setRunDistribution] = useState<TransformedRunDistributionData | null>(null);
  const [runOvertimeTrend, setRunOvertimeTrend] = useState<TransformedRunOvertimeData | null>(null);
  const [runOvertimeCards, setRunOvertimeCards] = useState<OvertimeCard[]>([]);
  const [jobTypeByStatusData, setJobTypeByStatusData] = useState<TransformedChartTableData | null>(null);
  const [scheduleByHourData, setScheduleByHourData] = useState<TransformedChartTableData | null>(null);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [tableData, setTableData] = useState<any[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [isTotalCountLoading, setIsTotalCountLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isKpiLoading, setIsKpiLoading] = useState(true);
  const [isScheduledKpiLoading, setIsScheduledKpiLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [groupBy, setGroupBy] = useState(() => sessionStorage.getItem(`${STORAGE_PREFIX}GroupBy`) || 'none');
  const [searchText, setSearchText] = useState('');
  const searchAfterRef = useRef<any>(null);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number | null>(() => {
    const value = sessionStorage.getItem(`${STORAGE_PREFIX}AutoRefresh`);
    return value === null ? 5 : Number(value) || null;
  });
  const [savedRangeType, setSavedRangeType] = useState<string | null>(() =>
    sessionStorage.getItem(`${STORAGE_PREFIX}DateRangeType`) || 'today',
  );
  const [savedTimeZone, setSavedTimeZone] = useState<string | null>(() =>
    sessionStorage.getItem(`${STORAGE_PREFIX}DateRangeTimeZone`) || 'CST',
  );
  const [startDate, setStartDate] = useState<string | null>(() =>
    sessionStorage.getItem(`${STORAGE_PREFIX}StartDate`),
  );
  const [endDate, setEndDate] = useState<string | null>(() =>
    sessionStorage.getItem(`${STORAGE_PREFIX}EndDate`),
  );
  const isFiltersLoadedRef = useRef(false);
  const dropdownsLoadedRef = useRef(false);
  const requestIdRef = useRef(0);
  const chartRequestIdRef = useRef(0);

  const setSearchAfter = useCallback((value: any) => {
    searchAfterRef.current = value;
  }, []);

  const onSavedFiltersLoaded = useCallback((savedFilters: FilterState) => {
    if (savedFilters) {
      const current = filterCache.getAppliedFilters();
      if (!current) {
        setAppliedFilters(savedFilters);
        filterCache.saveAppliedFilters(savedFilters);
      }
    }
    isFiltersLoadedRef.current = true;
  }, [filterCache]);

  const {
    isLoading: isFiltersLoading,
    isSaving: isSavingFilters,
    hasSavedFilters,
    savedFilters: savedDefaultFilters,
    manualSave: manualSaveFilters,
    resetToSaved: resetToSavedFilters,
  } = useDashboardFilterPersistence({
    config: DASHBOARD_CONFIG,
    currentFilters: appliedFilters,
    onFiltersLoaded: onSavedFiltersLoaded,
    filterCache,
  });

  const handleApplyFilters = useCallback((filters: FilterState) => {
    setAppliedFilters(filters);
    filterCache.saveAppliedFilters(filters);
    setPage(1);
    setSearchAfter(null);
    if (filters.jobStatus?.length === 1) setActiveTile(filters.jobStatus[0].value);
    else if (!filters.jobStatus?.length || filters.jobStatus.length > 1) {
      setActiveTile('TotalDataAssetRuns');
    }
  }, [filterCache]);

  const loadAllDropDowns = useCallback(async (
    _force = false,
    filtersOverride?: FilterState,
    field?: keyof FilterState,
  ) => {
    const filters = filtersOverride || appliedFilters;
    const globalFilters = toGlobalFilters(filters);
    const dateRange = makeDateRange(savedRangeType, startDate, endDate, savedTimeZone || 'CST');
    const fields = field ? [String(field)] : [...FILTER_FIELDS];
    if (field && loadingFilterFields[String(field)]) return;

    setLoadingFilterFields((previous: Record<string, boolean>) => ({
      ...previous,
      ...Object.fromEntries(fields.map((name) => [name, true])),
    }));
    try {
      const pageFilters = buildPageFilters({ activeTile, searchText });
      const result = await fetchFilterDetails({
        pageFilters,
        globalFilters,
        dateRange,
        dataAssetMode: 'runs',
        ...(field ? { field } : {}),
      });
      const data = result?.data?.data || {};
      setFilterOptions((previous: Record<string, any[]>) => ({ ...previous, ...data }));
    } catch (error) {
      console.error('Data Assets filter options could not be loaded:', error);
    } finally {
      setLoadingFilterFields((previous: Record<string, boolean>) => ({
        ...previous,
        ...Object.fromEntries(fields.map((name) => [name, false])),
      }));
    }
  }, [activeTile, appliedFilters, endDate, savedRangeType, savedTimeZone, searchText, startDate]);

  const loadKpis = useCallback(async () => {
    const globalFilters = toGlobalFilters(appliedFilters);
    const dateRange = makeDateRange(savedRangeType, startDate, endDate, savedTimeZone || 'CST');
    setIsKpiLoading(true);
    setIsScheduledKpiLoading(true);

    try {
      const [summaryResponse, uniqueResponse, scheduledResponse] = await Promise.all([
        fetchSummary({ globalFilters, dateRange, dataAssetMode: 'runs' }),
        fetchSummary({ globalFilters, dateRange, dataAssetMode: 'unique' }),
        fetchScheduleKpi({ globalFilters, dateRange, dataAssetMode: 'runs' }),
      ]);
      const summaryPayload = summaryResponse?.data || null;
      const uniquePayload = uniqueResponse?.data?.data || uniqueResponse?.data || {};
      if (summaryPayload?.data) {
        summaryPayload.data = {
          ...summaryPayload.data,
          uniqueDataAssetCount:
            uniquePayload.uniqueDataAssetCount ??
            uniquePayload.totalDataAssetRuns ??
            uniquePayload.dataAssetCount ??
            summaryPayload.data.uniqueDataAssetCount ?? 0,
          uniqueDataAssetCountLast1hr:
            uniquePayload.uniqueDataAssetCountLast1hr ??
            summaryPayload.data.uniqueDataAssetCountLast1hr ?? 0,
        };
      }
      setSummary(summaryPayload);
      setScheduledSummary(scheduledResponse?.data || null);
    } catch (error) {
      console.error('Data Assets KPI data could not be loaded:', error);
      setSummary(null);
      setScheduledSummary(null);
    } finally {
      setIsKpiLoading(false);
      setIsScheduledKpiLoading(false);
    }
  }, [appliedFilters, endDate, savedRangeType, savedTimeZone, startDate]);

  const loadCustomStatusKpis = useCallback(async () => {
    const globalFilters = toGlobalFilters(appliedFilters);
    const dateRange = makeDateRange(savedRangeType, startDate, endDate, savedTimeZone || 'CST');
    setIsCustomStatusKpiLoading(true);
    try {
      const result = await fetchCustomStatusCountKpi({
        pageFilters: { ...(searchText ? { searchText } : {}) },
        globalFilters,
        dateRange,
        dataAssetMode: 'runs',
      });
      setCustomStatusSummary(result?.data || null);
    } catch (error) {
      console.error('Data Assets custom status KPI data could not be loaded:', error);
      setCustomStatusSummary(null);
    } finally {
      setIsCustomStatusKpiLoading(false);
    }
  }, [appliedFilters, endDate, savedRangeType, savedTimeZone, searchText, startDate]);

  const loadJobs = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const globalFilters = toGlobalFilters(appliedFilters);
    const dateRange = makeDateRange(savedRangeType, startDate, endDate, savedTimeZone || 'CST');
    const { isCustomStatusTile, jobStatusList, cleanedColumnFilters } =
      resolveCustomStatusTile(activeTile, columnFilters, getValues(appliedFilters.jobStatus));
    const uniqueDataAssets = activeTile === 'UniqueDataAssetCount';
    const isShortRunClicked = activeTile === STATUS_FILTER_LABELS.SHORT_RUN;
    const effectiveSortingInfo = activeTile === 'Scheduled'
      ? (sortingInfo.sortBy ? sortingInfo : { sortBy: 'expected_start_time', sortDir: 'asc' })
      : (sortingInfo.sortBy && !['expected_start_time', 'last_run_start_time', 'schedule_missed'].includes(sortingInfo.sortBy)
        ? sortingInfo
        : { sortBy: '', sortDir: '' });
    const params: Record<string, any> = {
      pageFilters: {
        jobStatus: isShortRunClicked
          ? [STATUS_FILTER_LABELS.SUCCESSFUL, STATUS_FILTER_LABELS.FAILED]
          : isCustomStatusTile
            ? jobStatusList
            : [uniqueDataAssets || activeTile === 'TotalDataAssetRuns' ? 'All' : activeTile],
        ...(searchText ? { searchText } : {}),
        ...(isShortRunClicked ? { customStatus: STATUS_FILTER_LABELS.SHORT_RUN } : {}),
      },
      paginationInfo: {
        page,
        pageSize,
        ...(page > 1 && searchAfterRef.current ? { searchNext: searchAfterRef.current } : {}),
      },
      sortingInfo: effectiveSortingInfo,
      globalFilters: {
        ...globalFilters,
        ...(isCustomStatusTile ? { customStatus: activeTile } : {}),
      },
      dateRange,
      ...(cleanedColumnFilters.length
        ? { columnFilters: normalizeColumnFiltersForRequest(cleanedColumnFilters) }
        : {}),
      ...(groupBy !== 'none' ? { groupBy } : {}),
      dataAssetMode: uniqueDataAssets ? 'unique' : 'runs',
      ...(normalizeStatus(jobStatusList[0]) === 'inprogress' && !isCustomStatusTile
        ? { indexOverride: 'inProgressIndex' }
        : {}),
    };

    setIsLoading(true);
    setIsTotalCountLoading(true);
    try {
      const response = await fetchJobsList(params);
      if (requestId !== requestIdRef.current) return;
      const data = response?.data || {};
      const results = data.data;
      setTableData(data.isGrouped ? results?.groups || [] : Array.isArray(results) ? results : []);
      setTotalElements(Number(data.totalExecutions) || 0);
      setSearchAfter(data.searchNext || null);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error('Data Assets runs could not be loaded:', error);
      setTableData([]);
      setTotalElements(0);
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
        setIsTotalCountLoading(false);
      }
    }
  }, [
    activeTile,
    appliedFilters,
    columnFilters,
    endDate,
    groupBy,
    page,
    pageSize,
    savedRangeType,
    savedTimeZone,
    searchText,
    sortingInfo,
    startDate,
  ]);

  const loadActiveChart = useCallback(async () => {
    if (activeBottomTab === null) return;

    const requestId = ++chartRequestIdRef.current;
    const params = {
      globalFilters: toGlobalFilters(appliedFilters),
      dateRange: makeDateRange(savedRangeType, startDate, endDate, savedTimeZone || 'CST'),
      pageFilters: { ...(searchText ? { searchText } : {}) },
      dataAssetMode: 'runs',
    };
    setIsChartLoading(true);

    try {
      if (activeBottomTab === 0) {
        const response = await getRunDistribution(params);
        if (requestId !== chartRequestIdRef.current) return;
        const data = response?.data?.data;
        setRunDistribution({ result: Array.isArray(data) ? data : [] });
      } else if (activeBottomTab === 1) {
        const [trendResponse, cardsResponse] = await Promise.all([
          getRunTrend(params),
          getRunTrendAvg(params),
        ]);
        if (requestId !== chartRequestIdRef.current) return;
        setRunOvertimeTrend(
          trendResponse?.data?.data
            ? transformRunOvertimeTrendData(trendResponse.data.data)
            : null,
        );
        setRunOvertimeCards(
          cardsResponse?.data?.data
            ? transformRunTrendAvgToCards(cardsResponse.data.data)
            : [],
        );
      } else if (activeBottomTab === 2) {
        const response = await fetchJobStatusByType(params);
        if (requestId !== chartRequestIdRef.current) return;
        setJobTypeByStatusData(
          response?.data?.data ? transformJobTypeByStatusData(response.data.data) : null,
        );
      } else if (activeBottomTab === 3) {
        const response = await fetchJobScheduleByHour(params);
        if (requestId !== chartRequestIdRef.current) return;
        setScheduleByHourData(
          response?.data?.data ? transformScheduleByHourData(response.data.data) : null,
        );
      }
    } catch (error) {
      if (requestId !== chartRequestIdRef.current) return;
      console.error('Data Assets chart data could not be loaded:', error);
      if (activeBottomTab === 0) setRunDistribution({ result: [] });
      if (activeBottomTab === 1) {
        setRunOvertimeTrend(null);
        setRunOvertimeCards([]);
      }
      if (activeBottomTab === 2) setJobTypeByStatusData(null);
      if (activeBottomTab === 3) setScheduleByHourData(null);
    } finally {
      if (requestId === chartRequestIdRef.current) setIsChartLoading(false);
    }
  }, [
    activeBottomTab,
    appliedFilters,
    endDate,
    savedRangeType,
    savedTimeZone,
    searchText,
    startDate,
  ]);

  useEffect(() => {
    sessionStorage.setItem(`${STORAGE_PREFIX}GroupBy`, groupBy);
    setPage(1);
    setSearchAfter(null);
  }, [groupBy]);

  useEffect(() => {
    const syncSettings = () => {
      const refreshValue = sessionStorage.getItem(`${STORAGE_PREFIX}AutoRefresh`);
      setAutoRefreshInterval(refreshValue === null ? 5 : Number(refreshValue) || null);
      setSavedRangeType(sessionStorage.getItem(`${STORAGE_PREFIX}DateRangeType`));
      setSavedTimeZone(sessionStorage.getItem(`${STORAGE_PREFIX}DateRangeTimeZone`));
      setStartDate(sessionStorage.getItem(`${STORAGE_PREFIX}StartDate`));
      setEndDate(sessionStorage.getItem(`${STORAGE_PREFIX}EndDate`));
    };
    window.addEventListener('dataAssetsSessionStorageUpdated', syncSettings);
    const timer = window.setInterval(syncSettings, 500);
    return () => {
      window.removeEventListener('dataAssetsSessionStorageUpdated', syncSettings);
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const onSearch = (event: Event) => {
      const searchEvent = event as CustomEvent<{ searchText?: string }>;
      setSearchText(searchEvent.detail?.searchText || '');
      setPage(1);
      setSearchAfter(null);
    };
    window.addEventListener('pipelineSearch', onSearch);
    return () => window.removeEventListener('pipelineSearch', onSearch);
  }, []);

  useEffect(() => {
    setPage(1);
    setSearchAfter(null);
  }, [savedRangeType, startDate, endDate]);

  useEffect(() => {
    if (!isFiltersLoading) isFiltersLoadedRef.current = true;
  }, [isFiltersLoading]);

  useEffect(() => {
    if (isFiltersLoading || !isFiltersLoadedRef.current) return;
    void loadKpis();
    void loadCustomStatusKpis();
  }, [isFiltersLoading, loadKpis, loadCustomStatusKpis]);

  useEffect(() => {
    if (!isFiltersLoading && isFiltersLoadedRef.current) void loadJobs();
  }, [isFiltersLoading, loadJobs]);

  useEffect(() => {
    if (!isFiltersLoading && isFiltersLoadedRef.current && activeBottomTab !== null) {
      void loadActiveChart();
    }
    return () => {
      chartRequestIdRef.current += 1;
    };
  }, [activeBottomTab, isFiltersLoading, loadActiveChart]);

  useEffect(() => {
    if (!isFiltersLoadedRef.current || dropdownsLoadedRef.current) return;
    dropdownsLoadedRef.current = true;
    void loadAllDropDowns(false);
  }, [isFiltersLoading, loadAllDropDowns]);

  useEffect(() => {
    if (!isFiltersLoadedRef.current || !dropdownsLoadedRef.current) return;
    void loadAllDropDowns(true);
  }, [activeTile, searchText, savedRangeType, startDate, endDate, savedTimeZone]);

  useEffect(() => {
    if (!autoRefreshInterval) return;
    const timer = window.setInterval(() => {
      void loadKpis();
      void loadCustomStatusKpis();
      void loadJobs();
      if (activeBottomTab !== null) void loadActiveChart();
    }, autoRefreshInterval * 60 * 1000);
    return () => window.clearInterval(timer);
  }, [activeBottomTab, autoRefreshInterval, loadActiveChart, loadCustomStatusKpis, loadJobs, loadKpis]);

  const setSortingInfo = useCallback((next: { sortBy: string; sortDir: string }) => {
    setSortingInfoState(next);
    setPage(1);
    setSearchAfter(null);
  }, []);

  const handleColumnFiltersChange = useCallback((
    nextFilters: any[],
    nextSortingInfo: { sortBy: string; sortDir: string },
    nextFilterModel: FilterModel,
  ) => {
    setColumnFilters(nextFilters);
    setSortingInfoState(nextSortingInfo);
    setPage(1);
    setSearchAfter(null);
    if (nextFilters.length || nextSortingInfo.sortBy) {
      filterCache.saveColumnFilterState({
        filterModel: nextFilterModel,
        payload: nextFilters,
        sortModel: nextSortingInfo.sortBy
          ? [{ columnKey: nextSortingInfo.sortBy, direction: nextSortingInfo.sortDir }]
          : [],
      });
    } else {
      filterCache.clearColumnFilterState();
    }
  }, [filterCache]);

  const getColumnFilterValues = useCallback(async (field: string): Promise<any[]> => {
    if (field === 'custom_status' && activeTile !== STATUS_FILTER_LABELS.SHORT_RUN) {
      return [...CUSTOM_STATUS_OPTIONS];
    }
    if (field === 'custom_status' && activeTile === STATUS_FILTER_LABELS.SHORT_RUN) {
      return [STATUS_FILTER_LABELS.SHORT_RUN];
    }

    const result = await fetchFilterDetails({
      globalFilters: toGlobalFilters(appliedFilters),
      pageFilters: {
        jobStatus: [
          activeTile === 'TotalDataAssetRuns' || activeTile === 'UniqueDataAssetCount'
            ? 'All'
            : activeTile,
        ],
        ...(searchText ? { searchText } : {}),
      },
      ...(columnFilters.length
        ? { columnFilters: normalizeColumnFiltersForRequest(columnFilters) }
        : {}),
      dateRange: makeDateRange(savedRangeType, startDate, endDate, savedTimeZone || 'CST'),
      field,
      isColumnFilter: true,
      dataAssetMode: activeTile === 'UniqueDataAssetCount' ? 'unique' : 'runs',
    });

    const values = result?.data?.data?.[field];
    return Array.isArray(values) ? values : [];
  }, [
    activeTile,
    appliedFilters,
    columnFilters,
    endDate,
    savedRangeType,
    savedTimeZone,
    searchText,
    startDate,
  ]);

  const handleTileClick = useCallback((tile: string) => {
    setActiveTile(tile);
    setPage(1);
    setSearchAfter(null);
  }, []);

  const getExportRecordCount = useCallback(async (): Promise<number> => {
    try {
      const globalFilters = toGlobalFilters(appliedFilters);
      const dateRange = makeDateRange(savedRangeType, startDate, endDate, savedTimeZone || 'CST');
      const uniqueDataAssets = activeTile === 'UniqueDataAssetCount';
      const { isCustomStatusTile, jobStatusList, cleanedColumnFilters } =
        resolveCustomStatusTile(activeTile, columnFilters, getValues(appliedFilters.jobStatus));
      const isShortRunClicked = activeTile === STATUS_FILTER_LABELS.SHORT_RUN;
      const params = {
        pageFilters: {
          jobStatus: isShortRunClicked
            ? [STATUS_FILTER_LABELS.SUCCESSFUL, STATUS_FILTER_LABELS.FAILED]
            : isCustomStatusTile
              ? jobStatusList
              : [uniqueDataAssets || activeTile === 'TotalDataAssetRuns' ? 'All' : activeTile],
          ...(searchText ? { searchText } : {}),
          ...(isShortRunClicked ? { customStatus: STATUS_FILTER_LABELS.SHORT_RUN } : {}),
        },
        paginationInfo: { page: 1, pageSize: 1 },
        globalFilters: {
          ...globalFilters,
          ...(isCustomStatusTile ? { customStatus: activeTile } : {}),
        },
        ...(cleanedColumnFilters.length
          ? { columnFilters: normalizeColumnFiltersForRequest(cleanedColumnFilters) }
          : {}),
        dateRange,
        ...(groupBy !== 'none' ? { groupBy } : {}),
        dataAssetMode: uniqueDataAssets ? 'unique' : 'runs',
      };
      const response = await fetchExportCount(params);
      return Number(response?.data?.count) || 0;
    } catch (error) {
      console.error('Data Assets export count could not be loaded:', error);
      return 0;
    }
  }, [activeTile, appliedFilters, columnFilters, endDate, groupBy, savedRangeType, savedTimeZone, searchText, startDate]);

  const handleExport = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    const minDelay = new Promise((resolve) => setTimeout(resolve, 800));
    try {
      const uniqueDataAssets = activeTile === 'UniqueDataAssetCount';
      const { isCustomStatusTile, jobStatusList, cleanedColumnFilters } =
        resolveCustomStatusTile(activeTile, columnFilters, getValues(appliedFilters.jobStatus));
      const isShortRunClicked = activeTile === STATUS_FILTER_LABELS.SHORT_RUN;
      await Promise.all([exportJobsList({
        pageFilters: {
          jobStatus: isShortRunClicked
            ? [STATUS_FILTER_LABELS.SUCCESSFUL, STATUS_FILTER_LABELS.FAILED]
            : isCustomStatusTile
              ? jobStatusList
              : [uniqueDataAssets || activeTile === 'TotalDataAssetRuns' ? 'All' : activeTile],
          ...(searchText ? { searchText } : {}),
          ...(isShortRunClicked ? { customStatus: STATUS_FILTER_LABELS.SHORT_RUN } : {}),
        },
        paginationInfo: { page: 1, pageSize: 10000 },
        sortingInfo: sortingInfo.sortBy ? sortingInfo : { sortBy: '', sortDir: '' },
        formatType: 'EXCEL',
        globalFilters: {
          ...toGlobalFilters(appliedFilters),
          ...(isCustomStatusTile ? { customStatus: activeTile } : {}),
        },
        ...(cleanedColumnFilters.length
          ? { columnFilters: normalizeColumnFiltersForRequest(cleanedColumnFilters) }
          : {}),
        dateRange: makeDateRange(savedRangeType, startDate, endDate, savedTimeZone || 'CST'),
        groupBy: groupBy || 'none',
        dataAssetMode: uniqueDataAssets ? 'unique' : 'runs',
      }), minDelay]);
    } catch (error) {
      console.error('Data Assets export failed:', error);
    } finally {
      setIsExporting(false);
    }
  }, [
    activeTile,
    appliedFilters,
    columnFilters,
    endDate,
    groupBy,
    isExporting,
    savedRangeType,
    savedTimeZone,
    searchText,
    sortingInfo,
    startDate,
  ]);

  const fetchPdfData = useCallback(async (): Promise<any[]> => {
    const uniqueDataAssets = activeTile === 'UniqueDataAssetCount';
    const { isCustomStatusTile, jobStatusList, cleanedColumnFilters } =
      resolveCustomStatusTile(activeTile, columnFilters, getValues(appliedFilters.jobStatus));
    const isShortRunClicked = activeTile === STATUS_FILTER_LABELS.SHORT_RUN;
    const response = await fetchJobsList({
      pageFilters: {
        jobStatus: isShortRunClicked
          ? [STATUS_FILTER_LABELS.SUCCESSFUL, STATUS_FILTER_LABELS.FAILED]
          : isCustomStatusTile
            ? jobStatusList
            : [uniqueDataAssets || activeTile === 'TotalDataAssetRuns' ? 'All' : activeTile],
        ...(searchText ? { searchText } : {}),
        ...(isShortRunClicked ? { customStatus: STATUS_FILTER_LABELS.SHORT_RUN } : {}),
      },
      paginationInfo: { page: 1, pageSize: 500 },
      sortingInfo: sortingInfo.sortBy ? sortingInfo : { sortBy: '', sortDir: '' },
      globalFilters: {
        ...toGlobalFilters(appliedFilters),
        ...(isCustomStatusTile ? { customStatus: activeTile } : {}),
      },
      dateRange: makeDateRange(savedRangeType, startDate, endDate, savedTimeZone || 'CST'),
      ...(cleanedColumnFilters.length
        ? { columnFilters: normalizeColumnFiltersForRequest(cleanedColumnFilters) }
        : {}),
      ...(groupBy !== 'none' ? { groupBy } : {}),
      dataAssetMode: uniqueDataAssets ? 'unique' : 'runs',
    });
    const data = response?.data?.data;
    if (Array.isArray(data)) return data;
    return data?.groups || [];
  }, [activeTile, appliedFilters, columnFilters, endDate, groupBy, savedRangeType, savedTimeZone, searchText, sortingInfo, startDate]);

  const handleTileAndFilterApply = useCallback((filters: FilterState) => {
    handleApplyFilters(filters);
  }, [handleApplyFilters]);

  const dateRangeType = savedRangeType
    ? savedRangeType === 'custom'
      ? 'selected date range'
      : formatDateRangeLabel(savedRangeType)
    : '';

  return {
    activeTile,
    page,
    pageSize,
    sortingInfo,
    summary,
    scheduledSummary,
    customStatusSummary,
    isCustomStatusKpiLoading,
    activeBottomTab,
    setActiveBottomTab,
    runDistribution,
    runOvertimeTrend,
    runOvertimeCards,
    jobTypeByStatusData,
    scheduleByHourData,
    isChartLoading,
    tableData,
    totalElements,
    isTotalCountLoading,
    isLoading,
    isExporting,
    appliedFilters,
    filterOptions,
    loadingFilterFields,
    isInitialFilterLoading: isFiltersLoading,
    isFiltersLoading,
    isSavingFilters,
    hasSavedFilters,
    savedDefaultFilters,
    manualSaveFilters,
    resetToSavedFilters,
    groupBy,
    setGroupBy,
    loadAllDropDowns,
    handleApplyFilters: handleTileAndFilterApply,
    setPage,
    setPageSize,
    setSortingInfo,
    columnFilters,
    handleColumnFiltersChange,
    getColumnFilterValues,
    columnFilterModel: savedColumnFilters?.filterModel,
    handleTileClick,
    handleExport,
    getExportRecordCount,
    fetchPdfData,
    isKpiLoading,
    isScheduledKpiLoading,
    dateRangeType,
  };
};
